import React from 'react';
import Background   from '../components/Background';
import Rings        from '../components/Rings';
import Navbar       from '../components/Navbar';
import WordCycler   from '../components/WordCycler';
import WaitlistForm from '../components/WaitlistForm';
import { ABOUT_PARAGRAPHS, TEAM, DIRECTIONS, CONTACT } from '../content/landing';
import trlrLogo from '../assets/trlr logo black.png';

/*
 * Single-scroll page — no routing between sections.
 *
 * #hero    trlr · tagline · waitlist
 * #about   mission · team · directions
 * #contact get in touch
 *
 * Footer anchors scroll to each section.
 * Individual blog posts open via /blog/:slug.
 */

export default function Landing() {

  return (
    <div style={{ background: 'var(--bg)', position: 'relative' }}>

      {/* ── Fixed ambient layers ── */}
      <Background />
      <Rings />
      {/* <Grain /> */}
      <Navbar />

      {/* ── Content ── */}
      <div style={{ position: 'relative', zIndex: 10 }}>

        {/* ──────────────────────────────────────────
            HERO
        ────────────────────────────────────────── */}
        <section id="hero" style={{
          height:         '100vh',
          position:       'relative',
          pointerEvents:  'none',
        }}>
          {/* Wordmark — above center by --ring-gap */}
          <div style={{
            position:       'absolute',
            top:            '50%',
            left:           '50%',
            transform:      'translate(-50%, calc(-50% - var(--ring-gap)))',
            pointerEvents:  'auto',
            opacity:        0,
            animation:      'fadeIn 1s ease 0.2s forwards',
          }}>
            <img
              src={trlrLogo}
              alt="trlr"
              style={{ height: 'clamp(76px, 18vw, 200px)', width: 'auto', display: 'block' }}
            />
          </div>

          {/* Tagline — dead center */}
          <div style={{
            position:       'absolute',
            top:            '50%',
            left:           '50%',
            transform:      'translate(-50%, -50%)',
            pointerEvents:  'auto',
            fontSize:       'clamp(3rem, 8vw, 7.5rem)',
            fontFamily:     'var(--font-body)',
            fontWeight:     300,
            letterSpacing:  '-0.03em',
            lineHeight:     1,
            whiteSpace:     'nowrap',
            color:          'var(--text)',
            opacity:        0,
            animation:      'fadeIn 1s ease 0.5s forwards',
          }}>
            <span className="strike-it">it</span><WordCycler />
          </div>

          {/* Trial and waitlist — below center by --ring-gap */}
          <div style={{
            position:       'absolute',
            top:            '50%',
            left:           '50%',
            transform:      'translate(-50%, calc(-50% + var(--ring-gap) - 1vh))',
            pointerEvents:  'auto',
            opacity:        0,
            animation:      'fadeIn 1s ease 0.8s forwards',
          }} className="hero-trial">
            <div className="trial-stack" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.85rem' }}>
              <a
                href="https://trytrlr.com"
                style={{
                  fontFamily:     'var(--font-body)',
                  fontSize:       '0.9375rem',
                  fontWeight:     500,
                  letterSpacing:  '0.04em',
                  color:          'var(--bg)',
                  background:     'var(--text)',
                  textDecoration: 'none',
                  whiteSpace:     'nowrap',
                  padding:        '0.8rem 1.4rem',
                  borderRadius:   '6px',
                }}
              >
                Try TRLR →
              </a>
              <span className="trial-waitlist-label" style={{
                fontFamily:    'var(--font-body)',
                fontSize:      '0.75rem',
                color:         'var(--text-dim)',
                letterSpacing: '0.04em',
              }}>
                or join the waitlist
              </span>
              <WaitlistForm />
            </div>
          </div>
        </section>

        {/* ──────────────────────────────────────────
            ABOUT
        ────────────────────────────────────────── */}
        <section id="about" style={sectionWrap}>
          <div style={contentWrap}>

            <Eyebrow>About</Eyebrow>

            {ABOUT_PARAGRAPHS.map((paragraph, index) => (
              <p key={paragraph} style={index === ABOUT_PARAGRAPHS.length - 1 ? { ...bodyText, marginBottom: 0 } : bodyText}>
                {paragraph}
              </p>
            ))}

            <Divider />

            <SubHeading>Team</SubHeading>
            <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 3rem' }}>
              {TEAM.map(({ name, role, line }) => (
                <li key={name} style={{ marginBottom: '1.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.25rem' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.125rem', color: 'var(--color-text-primary)' }}>
                      {name}
                    </span>
                    <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: 'var(--text-dim)', letterSpacing: '0.03em' }}>
                      {role}
                    </span>
                  </div>
                  <p style={{ ...bodyText, margin: 0, fontSize: '0.9375rem' }}>{line}</p>
                </li>
              ))}
            </ul>

            <Divider />

            <SubHeading>What we’re building</SubHeading>
            {DIRECTIONS.map(({ name, desc }) => (
              <div key={name} style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.125rem', fontWeight: 400, color: 'var(--color-text-primary)', margin: '0 0 0.5rem' }}>
                  {name}
                </h3>
                <p style={{ ...bodyText, margin: 0 }}>{desc}</p>
              </div>
            ))}

          </div>
        </section>

        {/* ──────────────────────────────────────────
            CONTACT
        ────────────────────────────────────────── */}
        <section id="contact" style={sectionWrap}>
          <div style={contentWrap}>

            <Eyebrow>Contact</Eyebrow>

            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(2rem, 5vw, 3.5rem)', fontWeight: 300, letterSpacing: '-0.03em', lineHeight: 1.1, color: 'var(--text)', margin: '0 0 1.75rem' }}>
              {CONTACT.heading}
            </h2>
            <p style={{ ...bodyText, marginBottom: '2rem' }}>
              {CONTACT.description}
            </p>
            <a href={`mailto:${CONTACT.email}`} style={{
              fontFamily:     'var(--font-body)',
              fontSize:       'clamp(1rem, 2vw, 1.2rem)',
              color:          'var(--color-text-primary)',
              textDecoration: 'none',
              borderBottom:   '1px solid var(--color-input-border)',
              paddingBottom:  '2px',
              transition:     'border-color 0.2s',
            }}>
              {CONTACT.email}
            </a>
            <p style={{ ...bodyText, margin: '2rem 0 0' }}>{CONTACT.closing}</p>

          </div>
        </section>


      </div>
    </div>
  );
}

/* ── Layout primitives ────────────────────────────────────────────── */

const sectionWrap = {
  minHeight:      '100vh',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  borderTop:      '1px solid rgba(0,0,0,0.07)',
  padding:        'clamp(5rem, 12vw, 10rem) clamp(1.5rem, 6vw, 5rem)',
};

const contentWrap = {
  maxWidth: '700px',
  width:    '100%',
};

function Eyebrow({ children }) {
  return (
    <p style={{
      fontFamily:    'var(--font-display)',
      fontSize:      'clamp(2rem, 5vw, 3rem)',
      fontWeight:    400,
      color:         'var(--color-text-primary)',
      margin:        '0 0 2.5rem',
      letterSpacing: '0.01em',
    }}>
      {children}
    </p>
  );
}

function SubHeading({ children }) {
  return (
    <h2 style={{
      fontFamily:    'var(--font-display)',
      fontSize:      '1.375rem',
      fontWeight:    400,
      color:         'var(--color-text-primary)',
      margin:        '0 0 1.75rem',
      letterSpacing: '0.02em',
    }}>
      {children}
    </h2>
  );
}

function Divider() {
  return <hr style={{ border: 'none', borderTop: '1px solid var(--color-input-border)', margin: '3rem 0' }} />;
}

const bodyText = {
  fontFamily:   'var(--font-body)',
  fontSize:     '1rem',
  lineHeight:   1.75,
  color:        'var(--text)',
  marginBottom: '1.25rem',
};
